import CommonUtil from './common'

const defaultEscapeHtmlOptions = {
  nl2br: false,
  enableLink: false
}

export default class FormatUtil {

  static escapeHtml (text, options) {
    if (typeof text !== 'string') {
      return text
    }

    options = Object.assign({}, defaultEscapeHtmlOptions, options || {})

    let escapedText = options.nl2br
      ? text.replace(/\r?\n/g, '\\n')
      : text.substring(0)
    escapedText = escapedText.replace(/[&'`"<>]/g, (match) => {
      return {
        '&': '&amp;',
        '\'': '&#x27;',
        '`': '&#x60;',
        '"': '[SX-TAG:&quot;]',
        '<': '[SX-TAG:&lt;]',
        '>': '[SX-TAG:&gt;]'
      }[match]
    })

    if (options.nl2br) {
      escapedText = escapedText.replace(/\\n/g, '<br>')
    }

    if (options.enableLink) {
      escapedText = escapedText.replace(
        /(https?:\/\/[a-zA-Z0-9\-_.:@!~*'(¥);/?&=+$,%#]+)/gm,
        '<a href="$1" target="_blank">$1</a>')
      escapedText = escapedText.replace(/(<a[^>]+>)/gm, (match) => {
        return match.replace(/(&amp;)/g, '&')
      })
    }

    escapedText = escapedText.replace(/\[SX-TAG:([^\]]+)\]/gm, '$1')

    return escapedText
  }

  static escapeRegex (str) {
    return str.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
  }

  static capitalize (val) {
    if (CommonUtil.isEmpty(val)) {
      return ''
    }
    if (!CommonUtil.isString(val)) {
      return val
    }
    return val.charAt(0).toUpperCase() + val.slice(1)
  }

  static camelize (val) {
    if (CommonUtil.isEmpty(val)) {
      return ''
    }
    if (!CommonUtil.isString(val)) {
      return val
    }

    return val.split('_').map((value, index) => {
      return index <= 0 ? value : this.capitalize(value)
    }).join('')
  }

  static formatNumber (val, defaultVal = '') {
    if (CommonUtil.isEmpty(val, true)) {
      return String(val || defaultVal)
    }
    if (isNaN(Number(val))) {
      return String(val)
    }
    val = this.num2FracStr(val)
    const valInt = parseInt(String(val))
    if (Math.abs(valInt) >= 1000) {
      let tmpVal = valInt.toLocaleString()
      const indexDot = val.indexOf('.')
      if (indexDot >= 0) {
        tmpVal = tmpVal + val.substring(indexDot)
      }
      val = tmpVal
    }

    return val
  }

  static percentage (val, fractionDigits = 0) {
    if (CommonUtil.isEmpty(val, true)) {
      return ''
    }
    if (isNaN(Number(val))) {
      return String(val)
    }
    return `${Number(Number(val) * 100).toFixed(fractionDigits)}`
  }

  static num2FracStr (value) {
    if (CommonUtil.isEmpty(value, true)) {
      return ''
    }
    if (isNaN(Number(value))) {
      return String(value)
    }

    /*
     * 引数の値を文字列化
     */
    const numStr = String(value)
    if (numStr !== numStr.trim()) {
      return numStr
    }

    /*
     * 正規表現でマッチング
     */
    const match = numStr.match(
      /^([+-]?)0*([1-9][0-9]*|)(?:\.([0-9]*[1-9]|)0*)?(?:[eE]([+-]?[0-9]+))?$/)

    /*
     * 引数の型が適切な形式ではない場合…
     */
    if (!match) {
      if (typeof value === 'number') {
        /*
         * 引数の型が数値であれば、文字列化した値をそのまま返す
         */
        return numStr
      } else {
        /*
         * 引数の型が数値でなければ、エラーにする
         */
        throw new Error(`Invalid Value: "${numStr}"`)
      }
    }

    /** @type {string} 数の符号 */
    const sign = (match[1] === '-' ? '-' : '')
    /** @type {string} 仮数部の整数部 */
    const mantissaInt = match[2]
    /** @type {string} 仮数部の少数部 */
    const mantissaFrac = (match[3] ? match[3] : '')
    /** @type {number} 指数部 */
    const exponent = Number(match[4])

    let returnValue = ''

    if (exponent) {
      /*
       * exponentがundefinedではなく（正規表現で指数部がマッチしていて）、
       * かつ、0ではない場合、指数表記として処理を開始する
       *
       * Note: 指数部が0の場合、ここで処理する意味は無いので少数表記として処理する。
       *       よって、指数部が0以外の場合にここで処理する。
       * Note: undefinedは数値化されるとNaNになり、false相当となる。
       *       一方、0の場合もfalse相当となる。
       *       ので、↑の条件文はコレで合っている。
       */

      /** @type {string} */
      const mantissaStr = mantissaInt + mantissaFrac
      /** @type {number} */
      const mantissaLen = mantissaStr.length

      if (mantissaLen > 0) {
        /** @type {number} */
        const mantissaIntLen = mantissaInt.length + exponent

        /*
        12.145e+7  121450000             ;  mantissa_str: "12145"  mantissa_int_len: 9   ;  小数部が存在しない数値
        12.145e+6   12145000             ;  mantissa_str: "12145"  mantissa_int_len: 8   ;  小数部が存在しない数値
        12.145e+5    1214500             ;  mantissa_str: "12145"  mantissa_int_len: 7   ;  小数部が存在しない数値
        12.145e+4     121450             ;  mantissa_str: "12145"  mantissa_int_len: 6   ;  小数部が存在しない数値
        12.145e+3      12145             ;  mantissa_str: "12145"  mantissa_int_len: 5   ;  小数部が存在しない数値
        12.145e+2       1214.5           ;  mantissa_str: "12145"  mantissa_int_len: 4   ;  小数部が存在し、かつ、1より大きい数値
        12.145e+1        121.45          ;  mantissa_str: "12145"  mantissa_int_len: 3   ;  小数部が存在し、かつ、1より大きい数値
        12.145e0          12.145         ;  mantissa_str: "12145"  mantissa_int_len: 2   ;  小数部が存在し、かつ、1より大きい数値
        12.145e-1          1.2145        ;  mantissa_str: "12145"  mantissa_int_len: 1   ;  小数部が存在し、かつ、1より大きい数値
        12.145e-2          0.12145       ;  mantissa_str: "12145"  mantissa_int_len: 0   ;  小数部が存在し、かつ、1未満の数値
        12.145e-3          0.012145      ;  mantissa_str: "12145"  mantissa_int_len: -1  ;  小数部が存在し、かつ、1未満の数値
        12.145e-4          0.0012145     ;  mantissa_str: "12145"  mantissa_int_len: -2  ;  小数部が存在し、かつ、1未満の数値
        12.145e-5          0.00012145    ;  mantissa_str: "12145"  mantissa_int_len: -3  ;  小数部が存在し、かつ、1未満の数値
        12.145e-6          0.000012145   ;  mantissa_str: "12145"  mantissa_int_len: -4  ;  小数部が存在し、かつ、1未満の数値
        12.145e-7          0.0000012145  ;  mantissa_str: "12145"  mantissa_int_len: -5  ;  小数部が存在し、かつ、1未満の数値
        */

        if (mantissaLen <= mantissaIntLen) {
          /*
           * 小数部が存在しない数値（ex: 0, 12, 176, 1214500）の場合の処理
           */
          returnValue = mantissaStr
          if (mantissaStr.length < mantissaIntLen) {
            for (let i = 0, len = (mantissaIntLen - mantissaStr.length); i <
            len; i++) {
              returnValue = returnValue.concat('0')
            }
          }
        } else if (mantissaIntLen > 0) {
          /*
           * 小数部が存在し、かつ、1より大きい数値（ex: 1.26, 1.0009, 121.45）の場合の処理
           */
          returnValue = mantissaStr.slice(0, mantissaIntLen) + '.' +
            mantissaStr.slice(mantissaIntLen)

        } else {
          /*
           * 小数部が存在し、かつ、1未満の数値（ex: 0.26, 0.20098, 0.0012145）の場合の処理
           */
          returnValue = '0.' + '0'.repeat(-mantissaIntLen) + mantissaStr
        }
      }

    } else if (mantissaFrac) {
      /*
       * 少数表記の場合
       */
      returnValue = (mantissaInt || '0') + '.' + mantissaFrac

    } else if (mantissaInt) {
      /*
       * 整数表記の場合
       */
      returnValue = mantissaInt
    }

    return (returnValue) ? sign + (
      returnValue
      /* 先頭の余計なゼロを削除 */.replace(/^(?:0(?!\.|$))+/, '')
      /* 末尾の余計なゼロを削除 */.replace(/(?:\.0+|(\.[0-9]*[1-9])0+)$/, '$1')
    ) : '0'
  }

  static fileSize (fileSizeByte) {
    let ext = 'B'
    let value = CommonUtil.isArray(fileSizeByte) ? 0 : fileSizeByte

    if (CommonUtil.isArray(fileSizeByte)) {
      value = 0
      for (const tmpFileSizeByte of fileSizeByte) {
        value += tmpFileSizeByte
      }
    }

    if (value >= 1024) {
      ext = 'KB'
      value = value / 1024.0
    }
    if (value >= 1024) {
      ext = 'MB'
      value = value / 1024.0
    }
    if (value >= 1024) {
      ext = 'GB'
      value = value / 1024.0
    }

    // decimal one digit
    return `${(Math.round(value * 10) / 10)} ${ext}`
  }

}



// WEBPACK FOOTER //
// ./src/utils/format.js